/* eslint-disable no-underscore-dangle */
import { pick, omit } from 'lodash-es';

import { setMany } from '../../../utils/objectPath';

import SportradarService from '../../../services/sportradar';
import presetTemplates from '../../../presetTemplates/index';
import { resolverNames } from '../../../utils/sportradarConfigs';

/* eslint-disable no-shadow */
const state = () => ({
  isLoading: false,

  conferenceFilter: 'all', // used for stat selection

  // which settings to expose for user control
  controlOptions: {
    season: false,
    team: false,
    player: false,
    game: false,
  },

  customLabels: {},
  customFormats: {},

  elementId: null, // needed to update element data
  schemaInfo: {},
  leagueName: null,

  seasons: {},
  Season: {},

  teams: [],
  Team: {},
  statistics: {},
  teamLogo: null,

  players: [],
  Player: {},
  playerHeadshot: null,

  games: [],
  Game: {},
});

// Helper functions
/**
 * Transforms an array of team objects and maps each team to an object with its teamId as the key.
 * @param {Object[]} teams - The array of team objects to transform.
 * @returns {Object} An object with teamId as keys and team objects as values.
 */
function transformTeams(teams) {
  return teams.reduce((acc, team) => {
    acc[team.teamId] = team;
    return acc;
  }, {});
}

/**
 * Normalizes an array of game objects by converting the 'scheduledFor' property to a Date object and sorted.
 * It also restructures the array into an object with gameId as the key for each game.
 * @param {Object[]} games - The array of game objects to normalize.
 * @param {string} games[].gameId - The unique id for the game.
 * @param {string} games[].scheduledFor - Scheduled timestamp for the game, as a string or number.
 * @param {'team_v_team'|'players_pool'} games[].participantType - The type of participant in the game.
 * @returns {Object} Object with gameId as keys and game objects with 'scheduledFor' as values.
 */
function normalizeGames(games) {
  return games.map((game) => ({
    ...game,
    scheduledFor: new Date(Number(game.scheduledFor)),
  })).sort((a, b) => a.scheduledFor - b.scheduledFor);
}

/**
 * Transforms an array of season objects by filtering out seasons with a 'scheduled' status.
 * Then, it restructures the filtered array into an object with seasonId as the key for each season.
 * @param {Object[]} seasons - The array of season objects to transform.
 * @returns {Object} An object with seasonId as keys and filtered season objects as values.
 */
function transformSeasons(seasons) {
  return seasons.reduce((acc, season) => {
    acc[season.seasonId] = season;
    return acc;
  }, {});
}

/**
 * Maps GraphQL schema fields from an introspection query response.
 * This function processes the given introspection query response to filter and map the schema types
 * and their respective fields. It excludes certain types and system types (starting with '__').
 * @param {Object} introspectionResponse - The response object from a GraphQL introspection query.
 * @returns {Object} An object mapping each type name to its fields, with field types.
 */
function mapGqlSchemaFields(introspectionResponse) {
  const EXCLUDED_TYPE_NAMES = ['Query', 'Endpoints', 'RecordValue'];
  const { types } = introspectionResponse.__schema;
  const filteredTypes = types.filter((type) => {
    const { kind, name } = type;
    return kind === 'OBJECT' && !name.startsWith('__') && !EXCLUDED_TYPE_NAMES.includes(name);
  });
  const schemaTypesList = filteredTypes.map((t) => t.name);

  return filteredTypes.reduce((typeHash, currentType) => {
    const { name: typeName, fields } = currentType;

    const typeFields = {};

    fields.forEach((field) => {
      const { name: fieldName } = field;
      const { name: ofTypeName } = field?.type || {};
      typeFields[fieldName] = (schemaTypesList.includes(ofTypeName)) ? 'Object' : ofTypeName;
    });

    typeHash[typeName] = typeFields;

    return typeHash;
  }, {});
}

/**
 * Sorts an array of player objects by last name, then first name in ascending order.
 *
 * @param {Object[]} players - An array of player objects to be sorted.
 * @returns {Object[]} Array of player objects sorted by 'lastName' and 'firstName' properties ASC
 */
function sortPlayersByLastNameFirstName(players) {
  return [...players].sort((a, b) => {
    const lastNameComparison = a.lastName.localeCompare(b.lastName);
    if (lastNameComparison !== 0) {
      return lastNameComparison;
    }
    return a.firstName.localeCompare(b.firstName);
  });
}

const getters = {
  allSportradarData(state, getters) {
    // These need to be snake_case for formatting elsewhere
    // Only include data sets that have data (i.e. don't show "player" for team profile)
    const statType = state.controlOptions.player ? 'player' : 'team';
    const statPeriod = state.controlOptions.game ? 'game' : 'season';

    // TODO: get rid of statType and statPeriod in general in favor of just using resolverNames
    const isPlayerGameStats = statType === 'player' && statPeriod === 'game';
    const isPlayerSeasonStats = statType === 'player' && statPeriod === 'season';
    const isTeamGameStats = statType === 'team' && statPeriod === 'game';
    const isTeamSeasonStats = statType === 'team' && statPeriod === 'season';

    let statResolver;
    if (isPlayerGameStats) {
      statResolver = resolverNames.PlayerGameStats;
    } else if (isTeamGameStats) {
      statResolver = resolverNames.TeamGameStats;
    } else if (isPlayerSeasonStats) {
      statResolver = resolverNames.PlayerSeasonStats;
    } else if (isTeamSeasonStats) {
      statResolver = resolverNames.TeamSeasonStats;
    }

    return {
      ...(Object.keys(state.teams).length && { teams: state.teams }),
      ...(Object.keys(state.Team).length && { Team: state.Team }),
      ...(Object.keys(state.statistics).length && { [statResolver]: state.statistics }),
      ...(Object.keys(state.Player).length && { Player: state.Player }),
      ...(Object.keys(state.Game).length && { Game: state.Game }),
      ...getters.imageData,
    };
  },

  textData(state, getters) {
    // filter out non string/number data, and data that is not needed for text elements
    return omit(getters.allSportradarData, [
      'images',
      'Game.awayTeam.teamColorPrimary',
      'Game.awayTeam.teamColorSecondary',
      'Game.homeTeam.teamColorPrimary',
      'Game.homeTeam.teamColorSecondary',
      'Player.PlayerHeadshot',
      'teams',
      'Team.teamColors',
      'Team.teamColorPrimary',
      'Team.teamColorSecondary',
      'Team.TeamLogo',
    ]);
  },

  colorData(state, getters) {
    return pick(getters.allSportradarData, [
      'Team.teamColorPrimary',
      'Team.teamColorSecondary',
    ]);
  },

  imageData(state) {
    const imageObj = {
      images: {
        ...(getters.hasHeadshotPermission && { playerHeadshot: state.playerHeadshot }),
        ...(getters.hasLogoPermission && { teamLogo: state.teamLogo }),
      },
    };

    return Object.keys(imageObj.images).length ? imageObj : {};
  },

  getDataForProperty: (state, getters) => (type) => {
    switch (type) {
      case 'text':
        return getters.textData;
      case 'color':
      case 'backgroundColor':
      case 'gradientColorOne':
      case 'gradientColorTwo':
      case 'textShadowColor':
      case 'borderColor':
      case 'boxShadowColor':
        return getters.colorData;
      case 'backgroundImageUrl':
      case 'imageUrl':
        return getters.imageData;
      default:
        return omit(getters.sportradarData, ['teams']);
    }
  },

  getDataForElement: (state, getters) => (type) => {
    switch (type) {
      case 'image':
        return getters.imageData;
      default:
        return getters.textData;
    }
  },

  hasHeadshotPermission(state, getters, rootState) {
    return !!rootState.user?.apiKeys?.sportRadar?.some((k) => k.apiKeyName === `${state.leagueName}-headshot`);
  },

  hasLogoPermission(state, getters, rootState) {
    return !!rootState.user?.apiKeys?.sportRadar?.some((k) => k.apiKeyName === `${state.leagueName}-logo`);
  },

  // -=-=- translation and abstraction keys will need to be mirrored in producer -=-=-

  // for making abstracted paths point to actual data so we can change these options
  translationKeys(state) {
    return {
      '{{seasonId}}': state.Season?.seasonId,
      '{{teamId}}': state.Team?.teamId,
      '{{playerId}}': state.Player?.playerId,
    };
  },
  // for abstracting ids to translation keys for use above ^
  // Player.seasons.1234567890 -> Player.seasons.{{season}}
  abstractionKeys() {
    return {
      seasons: '{{seasonId}}',
      teams: '{{teamId}}',
      players: '{{playerId}}',
    };
  },
  dataFilters(state, getters) {
    // TODO: need to handle custom data differently here (once we do that)?
    return Object.entries(getters.abstractionKeys).reduce((acc, [key, value]) => {
      acc[key] = getters.translationKeys[value];
      return acc;
    }, {});
  },

  // Seasons sorted by year/type for dropdowns
  seasonsByYear(state) {
    const { seasons } = state;
    return Object.keys(seasons).map((s) => seasons[s]).sort((a, b) => {
      if (a.year < b.year) return 1;
      if (a.year > b.year) return -1;
      if (a.year === b.year) {
        return a.type?.code === 'REG' ? -1 : 1;
      }
    });
  },
};

const mutations = {
  /* eslint-disable no-param-reassign */
  SET_ID(state, value) {
    state.elementId = value;
  },
  SET_SCHEMA_INFO(state, value) {
    state.schemaInfo = value;
  },
  SET_LEAGUE_NAME(state, value) {
    state.leagueName = value;
  },
  SET_SEASONS(state, value) {
    state.seasons = value;
  },
  SET_SEASON(state, value) {
    state.Season = value;
  },
  SET_TEAMS(state, value) {
    state.teams = value;
  },
  SET_ELEMENT_STATS(state, value) {
    state.statistics = value;
  },
  SET_TEAM(state, value) {
    state.Team = value;
  },
  SET_PLAYER(state, value) {
    state.Player = value;
  },
  SET_PLAYERS(state, value) {
    state.players = value;
  },
  SET_GAME(state, value) {
    state.Game = value;
  },
  SET_GAMES(state, value) {
    state.games = value;
  },
  SET_PLAYER_HEADSHOT(state, value) {
    state.playerHeadshot = value;
  },
  SET_TEAM_LOGO(state, value) {
    state.teamLogo = value;
  },
  SET_CONTROL_OPTIONS(state, value) {
    setMany(state.controlOptions, value);
  },
  SET_CONFERENCE_FILTER(state, value) {
    state.conferenceFilter = value || 'all';
  },
};

const actions = {
  getSportradarData({ state, rootState }, { route, query }) {
    // shared request so we can add owner to all requests

    return SportradarService.getSportradarData({
      leagueName: state.leagueName,
      route,
      query: {
        owner: rootState.user.owner,
        ...query,
      },
    });
  },
  // Track data on the element for reconstructing queries later (producer, etc.)
  updateElementData({ dispatch, rootGetters, state }, payload) {
    const { key, value, blacklist = false } = payload;
    const element = rootGetters['graphicsTemplate/getElementById'](state.elementId);

    // Requests can outlive their graphic when users navigate or remove the element.
    if (!element) return;

    dispatch('graphicsTemplate/setOneElementValueByProp', {
      value: {
        id: state.elementId,
        propKey: `data.${key}`,
        propValue: value,
      },
      options: {
        blacklist,
      },
    }, { root: true });
  },
  updateControlOptions({ commit, dispatch }, payload) {
    const { statType, statPeriod } = payload;

    const season = true;
    const team = true;
    const player = true;
    const game = true;

    switch (true) {
      // player
      case (statType === 'player' && statPeriod === 'season'):
        dispatch('setControlOptions', { value: { season, team, player } });
        break;
      case (statType === 'player' && statPeriod === 'game'):
        dispatch('setControlOptions', {
          value: {
            season, team, player, game,
          },
        });
        break;
      // team
      case (statType === 'team' && statPeriod === 'season'):
        dispatch('setControlOptions', { value: { season, team } });
        break;
      case (statType === 'team' && statPeriod === 'game'):
        dispatch('setControlOptions', { value: { season, team, game } });
        break;

      default:
        break;
    }
  },
  async syncDataFromElement({ dispatch }, payload) {
    const {
      leagueName, seasonId, controlOptions, conferenceFilter,
    } = payload;
    dispatch('setControlOptions', { value: controlOptions, blacklist: true });
    dispatch('setConferenceFilter', { value: conferenceFilter, blacklist: true });
    if (leagueName) {
      dispatch('setLeagueAndGetSchema', { leagueName });

      // sync data in state for the element based on the provided IDs for stat selection.
      const statSelectActions = {
        seasonId: (seasonId) => dispatch('updateElementData', { key: 'seasonId', value: seasonId, blacklist: true }),
        teamId: (teamId) => dispatch('updateElementData', { key: 'teamId', value: teamId, blacklist: true }),
        playerId: (playerId) => dispatch('updateElementData', { key: 'playerId', value: playerId, blacklist: true }),
        gameId: (gameId) => dispatch('updateElementData', { key: 'gameId', value: gameId, blacklist: true }),
      };
      // Dispatch updates to element and commit based on provided IDs.
      Object.entries(payload).forEach(([key, value]) => {
        if (value?.length && statSelectActions[key]) {
          statSelectActions[key](value);
        }
      });
    }

    // return if all control options are false. Means element was just created
    // and don't need to sync from exsisiting element data.
    if (!Object.values(controlOptions).some((v) => v)) return;

    if (seasonId) {
      const statType = controlOptions.player ? 'player' : 'team';
      const statPeriod = controlOptions.game ? 'game' : 'season';

      // GraphQL stats data synchronization.
      Object.assign(payload, { leagueName, statType, statPeriod });
      await dispatch('syncGqlDataFromElement', payload);
    }
  },
  async syncGqlDataFromElement({
    commit,
    dispatch,
    getters,
    rootState,
  }, payload) {
    const opts = {
      ...payload,
      ownerId: rootState.user.owner,
    };

    const data = await SportradarService.getSportradarGraphQlData(opts);
    const {
      seasonId = payload.seasonId,
      teamId = payload.teamId,
      playerId = payload.playerId,
      gameId = payload.gameId,
      Season,
      Player,
      Team,
      Game,
    } = data || {};

    // Helper function to dispatch actions for logos and headshots
    const dispatchMedia = (type, id) => {
      const actionMap = {
        team: { permission: 'hasLogoPermission', action: 'getTeamLogo' },
        player: { permission: 'hasHeadshotPermission', action: 'getPlayerHeadshot' },
      };
      if (getters[actionMap[type].permission]) {
        dispatch(actionMap[type].action, { id });
      }
    };

    if (Season?.seasonId) {
      dispatch('setSeason', { value: Season, blacklist: true });
    }
    if (teamId) {
      if (Team?.TeamLogo?.imageUrl) {
        commit('SET_TEAM_LOGO', Team.TeamLogo.imageUrl);
      } else {
        dispatchMedia('team', teamId);
      }
    }
    if (Team) {
      dispatch('setTeam', { value: Team, blacklist: true });
    }

    if (playerId) {
      if (Player?.PlayerHeadshot?.imageUrl) {
        commit('SET_PLAYER_HEADSHOT', Player.PlayerHeadshot.imageUrl);
      } else {
        dispatchMedia('player', playerId);
      }
    }
    if (Player) {
      dispatch('setPlayer', { value: Player, blacklist: true });
    }

    if (gameId && Game) {
      dispatch('setGame', { value: Game, blacklist: true });
    }

    const statType = payload.statType || (payload.playerId ? 'player' : 'team');
    const statPeriod = payload.statPeriod || (payload.gameId ? 'game' : 'season');
    if (!data) {
      // TODO: stopgap for now. No need for an extra call for other stuff when no stats data exists.
      // probable solution is to update the resolver to return the names, etc, when no stats exist.
      const statSelectData = await SportradarService.getStatSelectionData({
        leagueName: payload.leagueName,
        ownerId: rootState.user.owner,
        teamId,
        seasonId,
        statPeriod,
        statType,
      });

      // Finds item by key or returns default.
      const findOrDef = (arr, key, id) => arr?.find((x) => x[key] === id) || { [key]: id };
      const Season = findOrDef(statSelectData.currentSeasons, 'seasonId', seasonId) || { seasonId };
      const Team = findOrDef(statSelectData.currentTeams, 'teamId', teamId) || { teamId };
      const Player = findOrDef(statSelectData.currentPlayers, 'playerId', playerId) || { playerId };
      const Game = findOrDef(statSelectData.currentGames, 'gameId', gameId) || { gameId };

      dispatch('setSeason', { value: Season, blacklist: true });
      dispatch('setTeam', { value: Team, blacklist: true });
      dispatch('setPlayer', { value: Player, blacklist: true });
      dispatch('setGame', { value: Game, blacklist: true });
      commit('SET_ELEMENT_STATS', {});
      return;
    }

    // Helper to remove '__typename' from GraphQL responses, if present
    const cleanStatistics = (statistics) => omit(statistics || {}, ['__typename']);
    if (statPeriod === 'season') {
      if (statType === 'player') {
        commit('SET_ELEMENT_STATS', {
          playerId,
          // Sportradar
          total: data.total,
          average: data.average,
          // SportStack
          statistics: cleanStatistics(data.statistics),
        });
      } else if (statType === 'team') {
        commit('SET_ELEMENT_STATS', {
          teamId,
          // Sportradar
          teamTotal: data.teamTotal,
          teamAverage: data.teamAverage,
          opponentsTotal: data.opponentsTotal,
          opponentsAverage: data.opponentsAverage,
          // SportStack
          statistics: cleanStatistics(data.statistics),
        });
      }
    } else if (statPeriod === 'game') {
      if (statType === 'player') {
        commit('SET_ELEMENT_STATS', { playerId, statistics: cleanStatistics(data.statistics) });
      } else if (statType === 'team') {
        commit('SET_ELEMENT_STATS', { teamId, statistics: cleanStatistics(data.statistics) });
      }
    }
  // end of syncGqlDataFromElement
  },

  async setLeagueAndGetSchema({ commit }, payload) {
    const { leagueName } = payload;

    commit('SET_LEAGUE_NAME', leagueName);

    const res = await SportradarService.getSchema(leagueName);

    const schema = mapGqlSchemaFields(res, leagueName);

    commit('SET_SCHEMA_INFO', schema);
    return res;
  },
  async getStatSelectionData({
    commit, dispatch, state, rootState,
  }, { newElement, statPeriod, statType }) {
    let res;
    try {
      res = await SportradarService.getStatSelectionData({
        leagueName: state.leagueName,
        ownerId: rootState.user.owner,
        teamId: state.Team?.teamId,
        seasonId: state.Season?.seasonId,
        statPeriod,
        statType,
      });
    } catch (err) {
      console.error('Error getting sportradar modal data', err);
      return undefined;
    }

    // Use helper functions
    const transformedSeasons = transformSeasons(res.allSeasons);
    const alphabetizedTeams = transformTeams(res.teamsForSeason);

    if (statType === 'player') {
      const currentPlayers = res.currentPlayers || [];
      const sortedPlayers = sortPlayersByLastNameFirstName(currentPlayers);
      commit('SET_PLAYERS', sortedPlayers);
      if (newElement) {
        dispatch('setPlayer', { value: sortedPlayers[0], blacklist: true });
      } else {
        const currentPlayer = sortedPlayers.find((p) => p.playerId === state.Player.playerId);
        if (currentPlayer) {
          dispatch('setPlayer', { value: currentPlayer, blacklist: true });
        }
      }
    }
    if (statPeriod === 'game') {
      const normalizedGames = normalizeGames(res.currentGames);
      commit('SET_GAMES', normalizedGames);
      if (newElement) {
        dispatch('setGame', { value: res.currentGame || res.currentGames[0], blacklist: true });
      } else {
        const currentGame = res.currentGames.find((g) => g.gameId === state.Game.gameId);
        if (currentGame) {
          dispatch('setGame', { value: currentGame, blacklist: true });
        }
      }
    }

    dispatch('setSeason', { value: res.currentSeason, blacklist: true });
    dispatch('setTeam', { value: res.currentTeam, blacklist: true });
    commit('SET_SEASONS', transformedSeasons);
    commit('SET_TEAMS', alphabetizedTeams);

    return res;
  // end of getStatSelectionData
  },
  async statSelectorChange({
    commit,
    state,
    rootState,
    dispatch,
  }, {
    seasonId,
    teamId,
    statPeriod,
    statType,
    conferenceFilter,
    selectionType,
  }) {
    const requestParams = {
      leagueName: state.leagueName,
      seasonId,
      teamId,
      ownerId: rootState.user.owner,
      statPeriod,
      statType,
    };

    if (selectionType === 'conference') {
      commit('SET_CONFERENCE_FILTER', conferenceFilter);
      return;
    }

    let res;
    if (selectionType === 'season') {
      res = await SportradarService.statSelectorSeasonChange(requestParams);
    }

    if (selectionType === 'team') {
      res = await SportradarService.statSelectorTeamChange(requestParams);
    }

    if (res.teamsForSeason?.length) {
      const alphabetizedTeams = transformTeams(res.teamsForSeason);
      commit('SET_TEAMS', alphabetizedTeams);
      dispatch('setSeason', { value: state.seasons[seasonId] });
    }

    if (statType === 'player') {
      const currentPlayers = res.currentPlayers || [];
      const sortedPlayers = sortPlayersByLastNameFirstName(currentPlayers);
      const currentPlayer = sortedPlayers[0] || {};
      commit('SET_PLAYERS', sortedPlayers);
      dispatch('setPlayer', { value: currentPlayer });
    }

    if (statPeriod === 'game' && res.currentGames?.length) {
      const normalizedGames = normalizeGames(res.currentGames);
      commit('SET_GAMES', normalizedGames);
      dispatch('setGame', { value: res.currentGame || res.currentGames[0] || {} });
    }

    return res;
  },
  async getTeamLogo({ commit, dispatch }, payload) {
    const {
      id,
    } = payload;

    // TODO: get height/width from payload and add to query

    try {
      const res = await dispatch('getSportradarData', {
        route: `images/team/${id}`,
        query: {},
      });
      commit('SET_TEAM_LOGO', res.image);
      return res;
    } catch (err) {
      console.error('Error getting team logo', err);
    }
    commit('SET_TEAM_LOGO', null);
    return undefined;
  },
  async getPlayerHeadshot({ commit, dispatch }, payload) {
    const {
      id,
    } = payload;

    // TODO: get height/width from payload and add to query

    try {
      const res = await dispatch('getSportradarData', {
        route: `images/player/${id}`,
        query: {},
      });

      commit('SET_PLAYER_HEADSHOT', res.image);
      return res;
    } catch (error) {
      console.error('Error getting player headshot', error);
    }

    commit('SET_PLAYER_HEADSHOT', null);
    return undefined;
  },

  createSportradarGraphicFromTemplate({ dispatch, getters }, payload) {
    const { provider, stats, templateSettings } = payload;
    const { theme, template } = templateSettings;
    const createdTemplate = presetTemplates[theme].templates[template].templateData({
      provider,
      stats,
      mappedPaths: getters.templateMappedPaths[template],
    });

    createdTemplate.elements.reverse().reduce(async (prevPromise, el) => {
      await prevPromise;
      const newEl = await dispatch('graphicsTemplate/addNewElement', {
        value: {
          type: el.type,
          settings: { ...el },
          parentId: el.parentId,
        },
        options: {
          blacklist: true,
        },
      }, { root: true });
      if (el.type === 'linked-data') {
        await dispatch('graphicsTemplate/setElementGroupMapById', {
          value: {
            id: newEl.id,
            childIds: [],
          },
          options: {
            blacklist: true,
          },
        }, { root: true });
      }
    }, Promise.resolve());
  },

  createUnstyledGraphic({ dispatch, getters, rootGetters }, payload) {
    const { provider, stats, statType } = payload;
    const canvas = rootGetters['graphicsTemplate/getCanvas'];

    const nonStatElements = getters.unstyledData[statType].map((el) => {
      const typeAndProp = {
        text: {
          type: 'text',
          property: 'text',
          settings: {},
        },
        image: {
          type: 'image',
          property: 'imageUrl',
        },
      };

      const { type, property } = typeAndProp[el.type];

      const obj = {
        type,
        parentId: provider,
      };

      obj.settings = {
        data: {
          provider,
          dataKeyMap: {
            [property]: el.path,
          },
        },
        ...el.settings,
      };

      obj.styles = el.styles ?? {};

      return obj;
    });
    const statElements = stats.reduce((arr, stat, i) => {
      const groupId = crypto.randomUUID();
      const labelId = crypto.randomUUID();
      const valueId = crypto.randomUUID();

      // linked group
      arr.push({
        type: 'linked-data',
        parentId: provider,
        settings: {
          id: groupId,
          name: `Stat ${i + 1}`,
        },
      });
      // label
      arr.push({
        type: 'text',
        parentId: groupId,
        settings: {
          id: labelId,
          name: `Label ${i + 1}`,
          text: `Label ${i + 1}`,
          data: {
            provider,
            dataKeyMap: {
              text: stat,
            },
            isLabel: true,
            linkedElement: valueId,
          },
        },
      });
      // value
      arr.push({
        type: 'text',
        parentId: groupId,
        settings: {
          id: valueId,
          name: `Value ${i + 1}`,
          text: `Stat ${i + 1}`,
          data: {
            provider,
            dataKeyMap: {
              text: stat,
            },
            linkedElement: labelId,
          },
        },
      });

      return arr;
    }, []);

    let widest = 0;
    let leftOffset = 0;
    let topOffset = 0;
    [...nonStatElements, ...statElements].reduce(async (prevPromise, el) => {
      await prevPromise;

      const newEl = await dispatch('graphicsTemplate/addNewElement', {
        value: {
          type: el.type,
          settings: {
            ...el.settings,
            ...(el.type !== 'linked-data' && {
              styles: {
                ...el.styles,
                left: leftOffset,
                top: topOffset,
              },
            }),
          },
          parentId: el.parentId,
          position: 'end',
        },
        options: {
          blacklist: true,
        },
      }, { root: true });

      if (el.type === 'linked-data') {
        await dispatch('graphicsTemplate/setElementGroupMapById', {
          value: {
            id: newEl.id,
            childIds: [],
          },
          options: {
            blacklist: true,
          },
        }, { root: true });
      } else {
        // check where the next element should go
        // eslint-disable-next-line no-lonely-if
        if ((topOffset + newEl.styles.height) > canvas.height) {
          // overflowing, start a new "column"
          leftOffset = widest;
          topOffset = 0;
          widest = 0;
        } else {
          // stick the next one below
          topOffset += newEl.styles.height;
          widest = Math.max(widest, newEl.styles.width);
        }
      }
    }, Promise.resolve());
  },

  setSeason({ commit, state }, { value, blacklist = false }) {
    commit('SET_SEASON', value);
    this.dispatch(`${state.elementId}/updateElementData`, { key: 'seasonId', value: value?.seasonId, blacklist });
  },

  setTeam({ commit, state }, { value, blacklist = false }) {
    commit('SET_TEAM', value);
    this.dispatch(`${state.elementId}/updateElementData`, { key: 'teamId', value: value?.teamId, blacklist });
  },

  setPlayer({ commit, state }, { value, blacklist = false }) {
    commit('SET_PLAYER', value);
    this.dispatch(`${state.elementId}/updateElementData`, { key: 'playerId', value: value?.playerId, blacklist });
  },

  setGame({ commit, state }, { value, blacklist = false }) {
    commit('SET_GAME', value);
    this.dispatch(`${state.elementId}/updateElementData`, { key: 'gameId', value: value?.gameId, blacklist });
  },

  setControlOptions({ commit, state }, { value, blacklist = false }) {
    commit('SET_CONTROL_OPTIONS', value);
    this.dispatch(`${state.elementId}/updateElementData`, { key: 'controlOptions', value: state.controlOptions, blacklist });
  },

  setConferenceFilter({ commit, state }, { value, blacklist = false }) {
    commit('SET_CONFERENCE_FILTER', value);
    this.dispatch(`${state.elementId}/updateElementData`, { key: 'conferenceFilter', value, blacklist });
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
